import React from 'react';
import { type ItemActionsProps, useModal } from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { DossierDocumentSign } from '../providers/types';
import { useDossierDocumentSignContext } from '../providers/DossierDocumentSignContext';
import useDocumentSign from './useDocumentSign';
import SelectDossierSignModal from '../components/shared/SelectDossierSignModal/SelectDossierSignModal';
import { DocumentColor } from './useDossierDocumentFilesApi';

type DossierStatus = 'pending' | 'failed' | 'done';

const useDocumentSignRender = () => {
    const { setSelectedDossier } = useDossierDocumentSignContext();
    const { downloadDocument } = useDocumentSign();
    const { modalActions } = useModal();

    const getDossierStatus = (dossier: DossierDocumentSign): DossierStatus => {
        const refusedCount = dossier.recipients.filter((recipient) => recipient.refused).length;
        const signedCount = dossier.recipients.filter((recipient) => recipient.signed).length;

        if (refusedCount > 0) {
            return 'failed';
        }

        if (signedCount === dossier.recipients.length) {
            return 'done';
        }

        return 'pending';
    };

    const getDossierColor = (dossier: DossierDocumentSign): DocumentColor => {
        const status = getDossierStatus(dossier);
        if (status === 'failed') {
            return 'error';
        }

        if (status === 'done') {
            return 'success';
        }

        return 'warning';
    };

    const getDossierAdditionalInfos = (dossier: DossierDocumentSign): Array<string> => {
        const buffer = [];
        const recipientsBuffer = dossier.recipients.map(
            (recipient) => `${recipient.firstName} ${recipient.lastName.toLocaleUpperCase()}`,
        );

        if (recipientsBuffer.length > 1) {
            const otherRecipientsCount = recipientsBuffer.length - 1;

            if (otherRecipientsCount > 1) {
                buffer.push(
                    `${recipientsBuffer[0]} et ${otherRecipientsCount} autres destinataires`,
                );
            } else {
                buffer.push(`${recipientsBuffer[0]} et ${otherRecipientsCount} autre destinataire`);
            }
        } else if (recipientsBuffer.length === 1) {
            buffer.push(`${recipientsBuffer[0]}`);
        }

        if (dossier.documents.length > 1) {
            const otherDocumentsCount = dossier.documents.length - 1;

            if (otherDocumentsCount > 1) {
                buffer.push(
                    `${dossier.documents[0].name} et ${otherDocumentsCount} autres documents`,
                );
            } else {
                buffer.push(
                    `${dossier.documents[0].name} et ${otherDocumentsCount} autre document`,
                );
            }
        } else if (recipientsBuffer.length === 1) {
            buffer.push(`${dossier.documents[0].name}`);
        }

        if (dossier.createdAt) {
            buffer.push(`Créé le ${dossier.createdAt.toLocaleDateString()}`);
        }

        return buffer;
    };

    const getDossierActions = (dossier: DossierDocumentSign): ItemActionsProps => ({
        onShow: (e) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            setSelectedDossier(dossier);
        },
        showTooltip: 'Voir le dossier',
    });

    const showSignedDocument = async (
        fileId: number,
        fileName: string,
        dossiers: Array<DossierDocumentSign>,
    ) => {
        if (dossiers.length > 1) {
            modalActions.call(
                <SelectDossierSignModal
                    dossiers={dossiers}
                    onSubmit={(dossier) => showSignedDocument(fileId, fileName, [dossier])}
                    onCancel={() => modalActions.close()}
                />,
            );
        } else {
            const dossier = dossiers.find(() => true);
            const document = (dossier?.documents || []).find((d) => d.fileId);

            if (document && document.signedFileId) {
                try {
                    await downloadDocument(fileName, document.signedFileId);
                } catch (e) {
                    console.error(e);
                    enqueueSnackbar(
                        'Une erreur est survenue lors du téléchargement de votre fichier',
                        { variant: 'error' },
                    );
                }
            }
        }
    };

    return { getDossierColor, getDossierAdditionalInfos, getDossierActions, showSignedDocument };
};

export default useDocumentSignRender;
